<template>
  <div id="admin-dashboard-container" class="pb-32 position-relative">
    <div v-if="!loaded" class="py-80 text-center">
      <v-loader />
    </div>

    <div v-else class="px-8">
      <!-- Title matching other pages e.g. cases (with black statistics icon) -->
      <div class="pt-24 pb-12 mb-20 border-bottom">
        <div class="h5 ff-secondary mb-0 d-flex align-items-center text-dark">
          <div class="me-8">
            <v-icon icon="statistics" size="xl" class="text-secondary" />
          </div>
          Dashboard
        </div>
      </div>

      <!-- Main Cases KPIs (Using official Waw Travels brand pink #ee2a7b and brand charcoal #2d2d2d) -->
      <div class="row g-20 mb-20">
        <!-- Total Cases -->
        <div class="col-sm-6 col-md-3">
          <div class="card p-24 border-0 shadow-sm rounded-lg kpi-card bg-white" @click="goToCasesWithFilter('all')">
            <div class="d-flex align-items-center justify-content-between mb-16">
              <span class="badge rounded bg-light-primary text-primary px-12 py-6 text-xs fw-bold">Total</span>
              <v-icon icon="briefcase" class="text-primary text-xl" />
            </div>
            <p class="text-muted text-sm fw-medium mb-4">Total Cases</p>
            <h3 class="fw-extrabold text-dark mb-0">{{ kpis.total_cases }}</h3>
          </div>
        </div>

        <!-- Active Cases -->
        <div class="col-sm-6 col-md-3">
          <div class="card p-24 border-0 shadow-sm rounded-lg kpi-card bg-white" @click="goToCasesWithFilter('active')">
            <div class="d-flex align-items-center justify-content-between mb-16">
              <span class="badge rounded bg-light-secondary text-secondary px-12 py-6 text-xs fw-bold">Active</span>
              <v-icon icon="check" class="text-secondary text-xl" />
            </div>
            <p class="text-muted text-sm fw-medium mb-4">Active Cases</p>
            <h3 class="fw-extrabold text-dark mb-0">{{ kpis.active_cases }}</h3>
          </div>
        </div>

        <!-- Draft Cases -->
        <div class="col-sm-6 col-md-3">
          <div class="card p-24 border-0 shadow-sm rounded-lg kpi-card bg-white" @click="goToCasesWithFilter('draft')">
            <div class="d-flex align-items-center justify-content-between mb-16">
              <span class="badge rounded bg-light-primary text-primary px-12 py-6 text-xs fw-bold">Drafts</span>
              <v-icon icon="pencil" class="text-primary text-xl" />
            </div>
            <p class="text-muted text-sm fw-medium mb-4">Draft Cases</p>
            <h3 class="fw-extrabold text-dark mb-0">{{ kpis.draft_cases }}</h3>
          </div>
        </div>

        <!-- Cancelled Cases -->
        <div class="col-sm-6 col-md-3">
          <div class="card p-24 border-0 shadow-sm rounded-lg kpi-card bg-white" @click="goToCasesWithFilter('cancelled')">
            <div class="d-flex align-items-center justify-content-between mb-16">
              <span class="badge rounded bg-light-secondary text-secondary px-12 py-6 text-xs fw-bold">Cancelled</span>
              <v-icon icon="close" class="text-secondary text-xl" />
            </div>
            <p class="text-muted text-sm fw-medium mb-4">Cancelled Cases</p>
            <h3 class="fw-extrabold text-dark mb-0">{{ kpis.cancelled_cases }}</h3>
          </div>
        </div>
      </div>

      <!-- Smaller & More Informative KPIs (Clients, and combined Supplier/Hotel Network) -->
      <div class="row g-16 mb-24">
        <!-- Client Base Box (col-md-4) -->
        <div class="col-sm-12 col-md-4">
          <div class="card px-16 py-12 border-0 shadow-sm rounded-md kpi-mini bg-white h-100">
            <div class="d-flex align-items-center gap-12 w-100 h-100">
              <div class="bg-light-secondary text-secondary rounded p-8 d-flex align-items-center justify-content-center">
                <v-icon icon="user" size="sm" />
              </div>
              <div class="flex-grow-1">
                <span class="text-muted text-xs fw-semibold d-block">Client Accounts</span>
                <span class="fw-bold text-dark text-sm">{{ kpis.clients_count }} Clients</span>
                <div class="mt-4 pt-4 border-top-divider">
                  <div class="d-flex justify-content-between align-items-center text-xs">
                    <span class="text-muted-light">Unique Travelers:</span>
                    <strong class="text-dark">{{ kpis.paxes_count }} pax</strong>
                  </div>
                  <div class="d-flex justify-content-between align-items-center text-xs mt-2">
                    <span class="text-muted-light">Avg. Group Size:</span>
                    <strong class="text-primary">{{ (kpis.paxes_count / (kpis.clients_count || 1)).toFixed(1) }} pax</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Combined Supplier & Hotel Network Box (col-md-8) -->
        <div class="col-sm-12 col-md-8">
          <div class="card px-16 py-12 border-0 shadow-sm rounded-md kpi-mini bg-white h-100">
            <div class="row align-items-center w-100 g-12 m-0 h-100">
              <!-- Left: Total Suppliers count -->
              <div class="col-sm-5 p-0 d-flex align-items-center gap-12 border-end-divider">
                <div class="bg-light-primary text-primary rounded p-8 d-flex align-items-center justify-content-center">
                  <v-icon icon="group" size="sm" />
                </div>
                <div>
                  <span class="text-muted text-xs fw-semibold d-block">Supplier Network</span>
                  <span class="fw-bold text-dark text-sm">{{ kpis.suppliers_count }} Partners</span>
                  <span class="text-muted x-small-desc mt-4 d-block">Active: <strong class="text-primary">{{ kpis.active_suppliers_count }}</strong> on dashboard</span>
                </div>
              </div>
              <!-- Right: Hotels breakdown and other services -->
              <div class="col-sm-7 p-0 ps-16 d-flex flex-column justify-content-center">
                <div class="d-flex justify-content-between align-items-baseline mb-4 text-xs">
                  <span class="text-muted-light"><span class="me-4">🏨</span> Hotels / Lodging:</span>
                  <strong class="text-dark">{{ kpis.hotels_count }}</strong>
                </div>
                <div class="d-flex justify-content-between align-items-baseline text-xs">
                  <span class="text-muted-light"><span class="me-4">🚌</span> Other Services:</span>
                  <strong class="text-dark">{{ kpis.suppliers_count - kpis.hotels_count }}</strong>
                </div>
                <p class="text-muted-light x-small-desc mb-0 leading-tight mt-4 italic">
                  Accommodations represent <strong>{{ Math.round((kpis.hotels_count / (kpis.suppliers_count || 1)) * 100) }}%</strong> of total supplier inventory.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Upcoming Cases Section (Placed ABOVE the metrics as requested) -->
      <div class="card p-24 mb-32 border-0 shadow-sm bg-white rounded-lg">
        <div class="d-flex align-items-center justify-content-between mb-16">
          <div>
            <h5 class="fw-bold text-dark mb-4 h6">Upcoming Cases (Next 2 Days)</h5>
            <p class="text-muted text-xs mb-0">Cases commencing today and the next two days.</p>
          </div>
          <span class="badge rounded bg-primary text-white px-12 py-6 text-xs fw-bold">
            {{ upcomingCases.length }} cases starting
          </span>
        </div>

        <!-- List -->
        <div v-if="upcomingCases.length === 0" class="py-24 text-center text-muted">
          <p class="mb-0 text-xs">No upcoming cases starting in the next 2 days.</p>
        </div>

        <div v-else class="upcoming-cases-list row g-12">
          <div v-for="wawCase in upcomingCases.slice(0, 2)" :key="wawCase.id" class="col-md-6">
            <div class="upcoming-case-item p-16 rounded border bg-white d-flex align-items-center justify-content-between">
              <div>
                <h6 class="fw-bold mb-4 text-dark text-sm">{{ wawCase.name }}</h6>
                <span class="text-muted text-xs d-flex align-items-center gap-4">
                  <v-icon icon="calendar" size="xs" /> Starts: {{ formatDate(wawCase.start_date) }}
                </span>
              </div>
              <v-button class="py-8 px-16 text-xs hover-lift" variant="primary" @click="routeToSetup(wawCase.id)">
                Case Setup
              </v-button>
            </div>
          </div>
        </div>
      </div>

      <!-- Metrics Charts Row (Side-by-side graphs) -->
      <div class="row g-24 mb-40">
        <!-- Chart 1: Last 4 Years Data (with left/right y-axis scales) -->
        <div class="col-lg-6">
          <div class="card p-24 border-0 shadow-sm bg-white rounded-lg h-100">
            <h5 class="fw-bold text-dark mb-4 h6">Last 4 Years Performance</h5>
            <p class="text-muted text-xs mb-24">Comparing cases processed vs net revenue made.</p>

            <svg viewBox="0 0 600 300" class="w-100" style="max-height: 220px; overflow: visible;">
              <!-- Grid lines -->
              <line x1="50" y1="50" x2="550" y2="50" stroke="#f2f2f2" stroke-width="1" />
              <line x1="50" y1="116" x2="550" y2="116" stroke="#f2f2f2" stroke-width="1" />
              <line x1="50" y1="183" x2="550" y2="183" stroke="#f2f2f2" stroke-width="1" />
              <line x1="50" y1="250" x2="550" y2="250" stroke="#dddddd" stroke-width="1.5" />

              <!-- Y-Axis Indices (Y-Axis Scale Indicators) -->
              <!-- Left Y-Axis index (Cases) -->
              <text x="40" y="254" text-anchor="end" class="chart-axis-text">0</text>
              <text x="40" y="187" text-anchor="end" class="chart-axis-text">{{ Math.round(maxCases() * 0.37) }}</text>
              <text x="40" y="120" text-anchor="end" class="chart-axis-text">{{ Math.round(maxCases() * 0.74) }}</text>
              <text x="40" y="54" text-anchor="end" class="chart-axis-text">{{ Math.round(maxCases()) }}</text>

              <!-- Right Y-Axis index (Revenue) -->
              <text x="560" y="254" text-anchor="start" class="chart-axis-text">€0</text>
              <text x="560" y="187" text-anchor="start" class="chart-axis-text">€{{ formatNumber(maxRevenue() * 0.37) }}</text>
              <text x="560" y="120" text-anchor="start" class="chart-axis-text">€{{ formatNumber(maxRevenue() * 0.74) }}</text>
              <text x="560" y="54" text-anchor="start" class="chart-axis-text">€{{ formatNumber(maxRevenue()) }}</text>

              <!-- Bars & Labels -->
              <g v-for="(stat, idx) in yearlyStats" :key="stat.year">
                <text :x="getXPos(idx) + 40" y="272" text-anchor="middle" class="text-xs fw-bold fill-dark">{{ stat.year }}</text>

                <!-- Cases Bar (Left, Pink) -->
                <rect
                  :x="getXPos(idx) + 16"
                  :y="getCaseY(stat.cases_count)"
                  width="22"
                  :height="getCaseHeight(stat.cases_count)"
                  fill="#ee2a7b"
                  rx="3"
                  class="chart-bar"
                  @mousemove="showTooltip($event, 'Year ' + stat.year, stat.cases_count + ' Cases Processed')"
                  @mouseleave="hideTooltip"
                />
                <text :x="getXPos(idx) + 27" :y="getCaseY(stat.cases_count) - 6" text-anchor="middle" class="chart-label fw-bold fill-dark">{{ stat.cases_count }}</text>

                <!-- Revenue Bar (Right, Charcoal) -->
                <rect
                  :x="getXPos(idx) + 42"
                  :y="getRevenueY(stat.revenue)"
                  width="22"
                  :height="getRevenueHeight(stat.revenue)"
                  fill="#2d2d2d"
                  rx="3"
                  class="chart-bar"
                  @mousemove="showTooltip($event, 'Year ' + stat.year, '€' + stat.revenue.toLocaleString() + ' Net Revenue')"
                  @mouseleave="hideTooltip"
                />
                <text :x="getXPos(idx) + 53" :y="getRevenueY(stat.revenue) - 6" text-anchor="middle" class="chart-label fw-bold fill-dark">€{{ formatNumber(stat.revenue) }}</text>
              </g>
            </svg>

            <!-- Legend (with padding added before labels: ps-8) -->
            <div class="d-flex justify-content-center gap-24 mt-20 pt-12 border-top">
              <div class="d-flex align-items-center text-xs">
                <div style="width: 10px; height: 10px; background-color: #ee2a7b;" class="rounded me-6"></div>
                <span class="text-muted fw-semibold ps-8">Case Count</span>
              </div>
              <div class="d-flex align-items-center text-xs">
                <div style="width: 10px; height: 10px; background-color: #2d2d2d;" class="rounded me-6"></div>
                <span class="text-muted fw-semibold ps-8">Net Revenue</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Chart 2: Month-wise cases count for selected year (Spline graph) -->
        <div class="col-lg-6">
          <div class="card p-24 border-0 shadow-sm bg-white rounded-lg h-100">
            <div class="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h5 class="fw-bold text-dark mb-4 h6">Monthly Distribution</h5>
                <p class="text-muted text-xs mb-24">Monthly number of cases for the selected year.</p>
              </div>
              <!-- Styled and high-contrast Year Selector -->
              <select v-model="selectedYear" @change="fetchMonthlyData" class="select-year-chart">
                <option v-for="yr in availableYears" :key="yr" :value="yr">{{ yr }}</option>
              </select>
            </div>

            <svg viewBox="0 0 600 300" class="w-100" style="max-height: 220px; overflow: visible;">
              <!-- Grid lines -->
              <line x1="50" y1="50" x2="550" y2="50" stroke="#f2f2f2" stroke-width="1" />
              <line x1="50" y1="116" x2="550" y2="116" stroke="#f2f2f2" stroke-width="1" />
              <line x1="50" y1="183" x2="550" y2="183" stroke="#f2f2f2" stroke-width="1" />
              <line x1="50" y1="250" x2="550" y2="250" stroke="#dddddd" stroke-width="1.5" />

              <!-- Y-Axis index scale -->
              <text x="40" y="254" text-anchor="end" class="chart-axis-text">0</text>
              <text x="40" y="187" text-anchor="end" class="chart-axis-text">{{ Math.round(maxMonthlyCases() * 0.37) }}</text>
              <text x="40" y="120" text-anchor="end" class="chart-axis-text">{{ Math.round(maxMonthlyCases() * 0.74) }}</text>
              <text x="40" y="54" text-anchor="end" class="chart-axis-text">{{ Math.round(maxMonthlyCases()) }}</text>

              <!-- Spline Curve Path (Pink #ee2a7b) -->
              <path :d="getSplinePath()" fill="none" stroke="#ee2a7b" stroke-width="3.5" />

              <!-- Dots & Labels -->
              <g v-for="(stat, idx) in monthlyStats" :key="'dot-'+stat.month">
                <!-- Circular point marker -->
                <circle
                  :cx="getXPosMonthly(idx) + 10"
                  :cy="getMonthlyY(stat.count)"
                  r="5"
                  fill="#fff"
                  stroke="#ee2a7b"
                  stroke-width="2.5"
                  class="chart-dot"
                  @mousemove="showTooltip($event, stat.month + ' ' + selectedYear, stat.count + ' Cases Commenced')"
                  @mouseleave="hideTooltip"
                />
                <!-- Month label on X Axis -->
                <text :x="getXPosMonthly(idx) + 10" y="272" text-anchor="middle" class="chart-label fw-bold fill-dark">{{ stat.month }}</text>
                <!-- Count value label -->
                <text :x="getXPosMonthly(idx) + 10" :y="getMonthlyY(stat.count) - 10" text-anchor="middle" class="chart-label fw-bold fill-dark">{{ stat.count }}</text>
              </g>
            </svg>

            <!-- Legend (with padding added before labels: ps-8) -->
            <div class="d-flex justify-content-center mt-20 pt-12 border-top">
              <div class="d-flex align-items-center text-xs">
                <div style="width: 10px; height: 10px; background-color: #ee2a7b;" class="rounded me-6"></div>
                <span class="text-muted fw-semibold ps-8">Case Count ({{ selectedYear }})</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Rich typography HTML Tooltip overlay -->
      <div
        v-if="tooltip.show"
        class="custom-tooltip p-12"
        :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }"
      >
        <div class="text-xs text-muted fw-bold text-uppercase tracking-wider mb-4">{{ tooltip.title }}</div>
        <div class="text-sm text-dark fw-extrabold">{{ tooltip.value }}</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import api from '@/api';
import VLoader from '@/components/vendor/loader/VLoader.vue';
import VButton from '@/components/vendor/basic/button/VButton.vue';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';

export default defineComponent({
  name: 'TheAdminDashboard',
  components: {
    VLoader,
    VButton,
    VIcon,
  },
  data() {
    return {
      loaded: false as boolean,
      kpis: {
        total_cases: 0,
        active_cases: 0,
        draft_cases: 0,
        cancelled_cases: 0,
        clients_count: 0,
        suppliers_count: 0,
        hotels_count: 0,
        paxes_count: 0,
        active_suppliers_count: 0,
      },
      upcomingCases: [] as any[],
      caseManagers: [] as any[],
      yearlyStats: [] as any[],
      monthlyStats: [] as any[],
      selectedYear: new Date().getFullYear() as number,
      availableYears: [] as number[],
      filters: {
        year: new Date().getFullYear() as number,
      },
      tooltip: {
        show: false as boolean,
        x: 0 as number,
        y: 0 as number,
        title: '' as string,
        value: '' as string,
      }
    };
  },
  async created() {
    await this.fetchDashboardData();
  },
  methods: {
    async fetchDashboardData() {
      try {
        const response = await api.adminDashboard.getDashboardData(this.filters);
        const { data } = response;
        this.kpis = data.kpis;
        this.upcomingCases = data.upcoming_cases;
        this.caseManagers = data.case_managers;
        this.yearlyStats = data.yearly_stats || [];
        this.monthlyStats = data.monthly_stats || [];
        this.selectedYear = data.selected_year;
        this.availableYears = data.available_years || [this.selectedYear];
      } catch (error) {
        console.error('Error loading admin dashboard stats:', error);
        this.$toast.error('Failed to load dashboard metrics.');
      } finally {
        this.loaded = true;
      }
    },
    async fetchMonthlyData() {
      this.filters.year = this.selectedYear;
      await this.fetchDashboardData();
    },
    formatDate(date: string) {
      if (!date) return '';
      const d = new Date(date);
      if (isNaN(d.getTime())) return date;
      const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
      return d.toLocaleDateString('en-GB', options);
    },
    routeToSetup(caseId: number) {
      this.$router.push({
        name: 'case-v2.setup.show',
        params: {
          caseId,
        },
      });
    },
    goToCasesWithFilter(type: string) {
      const query: any = {};
      if (type === 'active') {
        query['filters[is_cancelled]'] = '0';
        query['filters[is_draft]'] = '0';
      } else if (type === 'draft') {
        query['filters[is_draft]'] = '1';
      } else if (type === 'cancelled') {
        query['filters[is_cancelled]'] = '1';
      }
      this.$router.push({
        name: 'case-v2.index',
        query,
      });
    },
    maxCases(): number {
      const vals = this.yearlyStats.map((s: any) => s.cases_count);
      return Math.max(...vals, 10);
    },
    maxRevenue(): number {
      const vals = this.yearlyStats.map((s: any) => s.revenue);
      return Math.max(...vals, 1000);
    },
    getXPos(idx: number): number {
      return 50 + (idx * 125);
    },
    getCaseHeight(count: number): number {
      const max = this.maxCases();
      return (count / max) * 180;
    },
    getCaseY(count: number): number {
      return 250 - this.getCaseHeight(count);
    },
    getRevenueHeight(rev: number): number {
      const max = this.maxRevenue();
      return (rev / max) * 180;
    },
    getRevenueY(rev: number): number {
      return 250 - this.getRevenueHeight(rev);
    },
    maxMonthlyCases(): number {
      const vals = this.monthlyStats.map((s: any) => s.count);
      return Math.max(...vals, 10);
    },
    getXPosMonthly(idx: number): number {
      // 12 months spaced across x = 55 to 550
      // distance: 495, 495 / 11 = 45px
      return 55 + (idx * 45);
    },
    getMonthlyHeight(count: number): number {
      const max = this.maxMonthlyCases();
      return (count / max) * 180;
    },
    getMonthlyY(count: number): number {
      return 250 - this.getMonthlyHeight(count);
    },
    formatNumber(num: number): string {
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
      }
      if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'k';
      }
      return num.toFixed(0);
    },
    getSplinePath(): string {
      if (this.monthlyStats.length === 0) return '';

      let path = '';
      const points = this.monthlyStats.map((stat: any, idx: number) => {
        return {
          x: this.getXPosMonthly(idx) + 10,
          y: this.getMonthlyY(stat.count)
        };
      });

      path += `M ${points[0].x} ${points[0].y}`;

      for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[i];
        const p1 = points[i + 1];
        const cp1x = p0.x + (p1.x - p0.x) / 3;
        const cp1y = p0.y;
        const cp2x = p0.x + 2 * (p1.x - p0.x) / 3;
        const cp2y = p1.y;

        path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p1.x} ${p1.y}`;
      }

      return path;
    },
    showTooltip(event: MouseEvent, title: string, value: string) {
      const container = document.getElementById('admin-dashboard-container');
      if (container) {
        const rect = container.getBoundingClientRect();
        this.tooltip.x = event.clientX - rect.left + 15;
        this.tooltip.y = event.clientY - rect.top + 15;
      }
      this.tooltip.title = title;
      this.tooltip.value = value;
      this.tooltip.show = true;
    },
    hideTooltip() {
      this.tooltip.show = false;
    }
  },
});
</script>

<style lang="scss" scoped>
#admin-dashboard-container {
  max-width: 1550px;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1.25rem;
  padding-right: 1.25rem;
  background-color: #ffffff;
  min-height: 100vh;
}

@media (min-width: 1400px) {
  #admin-dashboard-container {
    padding-left: 1.75rem;
    padding-right: 1.75rem;
  }
}

.kpi-card {
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border-radius: 12px;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08) !important;
  }
}

.kpi-mini {
  border-radius: 8px;
  border: 1px solid #edf2f7;
  height: 80px;
  display: flex;
  justify-content: center;
  align-content: center;
}

.border-end-divider {
  border-right: 1px solid #edf2f7;
}

.border-top-divider {
  border-top: 1px solid #edf2f7;
}

.select-year-chart {
  width: 90px !important;
  height: 32px !important;
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  color: #2d3748 !important;
  background-color: #fff !important;
  border: 1.5px solid #cbd5e0 !important;
  border-radius: 6px !important;
  padding: 2px 8px !important;
  display: block !important;
  cursor: pointer;

  &:focus {
    border-color: #ee2a7b !important;
    outline: 0;
  }
}

// Brand theme support colors using Waw Travels brand colors: Pink (#ee2a7b) & Charcoal (#2d2d2d)
.text-primary { color: #ee2a7b !important; }
.bg-light-primary { background-color: rgba(238, 42, 123, 0.08); }
.bg-light-secondary { background-color: rgba(45, 45, 45, 0.08); }
.bg-light-danger { background-color: rgba(212, 40, 40, 0.08); }

.x-small-desc {
  font-size: 0.7rem;
  color: #718096;
}

.xx-small { font-size: 0.65rem; }
.text-xs { font-size: 0.75rem; }
.text-sm { font-size: 0.875rem; }

.upcoming-case-item {
  transition: background-color 0.15s ease;
  border: 1px solid #cbd5e0 !important;
  background-color: #ffffff;
  border-radius: 8px;
  &:hover {
    background-color: #f7fafc;
  }
}

.hover-lift {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(238, 42, 123, 0.2) !important;
  }
}

.chart-bar {
  transition: height 0.3s ease, y 0.3s ease;
  cursor: pointer;
  &:hover {
    opacity: 0.85;
  }
}

.chart-dot {
  transition: r 0.2s ease;
  cursor: pointer;
  &:hover {
    r: 7;
  }
}

.chart-label {
  font-size: 8px;
}

.chart-axis-text {
  font-size: 9px;
  fill: #718096;
  font-weight: 500;
}

.fill-dark {
  fill: #2d2d2d;
}

.italic {
  font-style: italic;
}

/* Premium Glassmorphic Tooltip styling */
.custom-tooltip {
  position: absolute;
  pointer-events: none;
  z-index: 1050;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(4px);
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
  min-width: 140px;
  transition: opacity 0.1s ease;
  font-family: inherit;
}
</style>
